<template>
  <div>
  <!-- Hero section -->
  <section class="relative overflow-hidden hero-xweather-gradient-bg">
    <div class="mx-auto mt-20 flex max-w-6xl flex-col gap-10 px-6 py-12 md:flex-row md:items-center md:py-16 lg:py-20">
      <!-- Left column -->
      <div class="md:w-1/2 space-y-6">
        <div class="space-y-3">
          <img :src="imgSrc($t('caseStudies.CS_xweather.logoProduct'))" alt="Amazon logo" class="w-16"/>
          <h1 class="text-3xl font-semibold sm:text-4xl lg:text-5xl text-slate-700 lg:leading-[1.2]">
            {{ $t('caseStudies.CS_xweather.title') }}
          </h1>
          <p class="max-w-xl text-md text-slate-700 sm:text-base lg:text-lg">
            {{ $t('caseStudies.CS_xweather.title_description') }}
          </p>
        </div>

        <!-- Tags -->
        <div class="flex flex-wrap gap-2 md:gap-4 pt-2">
          <span v-for="(tag, idx) in $tm('caseStudies.CS_xweather.tags')" :key="idx" class="inline-flex items-center rounded-full border border-slate-400 px-3 py-1 text-xs font-medium uppercase tracking-wide text-slate-600">{{tag}}</span>
        </div>
      </div>

      <!-- Right column -->
      <div class="md:w-1/2 flex justify-center hidden md:block">
        <img :src="imgSrc($t('caseStudies.CS_xweather.heroImage'))" 
        alt="three phone screens with xweather app forecasting different weather" 
        class="max-w-[350px]"/>
      </div>
    </div>
  </section>
  <!-- End of Hero section -->



  <!-- intro-section -->
  <section class="flex flex-col text-normal leading-normal mx-auto max-w-6xl px-6 py-8 dark:text-white">
    <ul class="list-none flex flex flex-wrap gap-y-4 gap-x-8 pt-2 items-end justify-start text-xl dark:text-white">
      <!-- Role detail -->
      <li class="flex flex-col text-slashdark dark:text-white">
          <span class="text-lg">{{ $t('caseStudies.CS_xweather.myRole.title') }}</span> 
          <span class="font-semibold text-lg">{{ $t('caseStudies.CS_xweather.myRole.description') }}</span> 
      </li>
    </ul>
    <p class="text-slashdark font-light text-xl leading-normal py-4 lg:text-2xl lg:leading-normal dark:text-white">{{ $t('caseStudies.CS_xweather.intro-section') }}</p>
  </section>
  <!-- End/intro-section -->


    <!-- Brief-section -->
    <section class="flex flex-col gap-4 lg:flex-row lg:gap-8 text-normal leading-normal mx-auto max-w-6xl px-6 py-8 dark:text-white">
      <div class="flex flex-col w-full lg:basis-2/3 space-y-4">
        <h2 class="text-2xl h-auto max-w-prose dark:text-white">{{ $t('caseStudies.CS_xweather.brief-section.title') }}</h2>
        <p class="mb-4 text-base text-slate-700 leading-relaxed dark:text-slate-300">{{ $t('caseStudies.CS_xweather.brief-section.content') }}</p>
      </div>
      <ul class="flex flex-row flex-wrap items-end justify-start h-auto lg:mt-10 lg:basis-1/3 h-[50%]">
        <li v-for="(skill,idx) in $tm('caseStudies.CS_xweather.brief-section.skills-used')" :key="idx" class="font-medium text-lg px-4 py-2 text-slashblue dark:text-white">
          {{skill}}
        </li>
      </ul>
    </section>
    <!-- END/ Brief-section -->
    
    <!-- Logo-section -->
    <section class="flex flex-col gap-4 bg-slate-100 lg:rounded-lg lg:flex-row lg:gap-8 text-normal leading-normal mx-auto max-w-[1134px] px-6 py-8 mb-6">
      <div class="flex flex-col lg:basis-2/3 space-y-4">
        <h2 class="text-2xl h-auto max-w-prose">{{ $t('caseStudies.CS_xweather.logo-section.title') }}</h2>
        <p class="mb-4 text-base text-slate-700 leading-relaxed">{{ $t('caseStudies.CS_xweather.logo-section.content') }}</p>
      </div>
      <div class="flex w-full lg:basis-1/3 justify-center items-center">
        <img 
        :src="imgSrc($t('caseStudies.CS_xweather.logo-section.image.url'))" 
        :alt="$t('caseStudies.CS_xweather.logo-section.image.alt')" 
        class="max-w-[310px] md:max-w-sm"/>
      </div>
    </section>
    <!-- END/ Logo-section -->


    <!-- Logo-icons+colours -->
    <section class="flex flex-col gap-4 lg:flex-row text-normal leading-normal mx-auto max-w-[1134px] mb-6">
     
     <!-- Icons -->
      <div class="flex flex-col bg-slate-100 lg:rounded-lg p-8 justify-between">
        <div class="space-y-4">
          <h2 class="text-2xl h-auto max-w-prose">{{ $t('caseStudies.CS_xweather.design-icons-section.title') }}</h2>
          <p class="mb-4 text-base text-slate-700 leading-relaxed">{{ $t('caseStudies.CS_xweather.design-icons-section.content') }}</p>
        </div>
        <ul class="grid grid-cols-4 gap-4 justify-start items-center">
          <li v-for="(icon,idx) in $tm('caseStudies.CS_xweather.design-icons-section.icons')" :key="idx" class="max-w-[100px]">
            <img :src="iconSrc(icon.url)" 
            :alt="icon.alt" 
            :class="`${icon.style}`"/>
          </li>
        </ul>
      </div>
      <!-- END/Icons -->

      <!-- Colours -->
      <div class="flex flex-col bg-slate-100 lg:rounded-lg p-8">
        <div class="space-y-4">
          <h2 class="text-2xl h-auto max-w-prose">{{ $t('caseStudies.CS_xweather.exploring-colours-section.title') }}</h2>
          <p class="mb-4 text-base text-slate-700 leading-relaxed">{{ $t('caseStudies.CS_xweather.exploring-colours-section.content') }}</p>
        </div>
        <ul class="grid grid-cols-3 gap-4 mt-12">
          <li v-for="(img,idx) in $tm('caseStudies.CS_xweather.exploring-colours-section.images')" :key="idx" class="">
            <img :src="iconSrc(img.url)" 
            :alt="img.alt" 
            class="w-full"/>
          </li>
        </ul>
      </div>
      <!-- Colours -->
    </section>
    <!-- END/Logo-icons+colours -->

    <!-- Results section -->
    <section class="flex flex-col gap-4 lg:flex-row lg:gap-8 lg:rounded-lg  bg-slate-100 text-normal leading-normal mx-auto max-w-[1134px] mb-24 p-8">
      <div class="flex w-full lg:basis-1/3 justify-center items-center">
        <img 
        :src="imgSrc($t('caseStudies.CS_xweather.results-section.image.url'))" 
        :alt="$t('caseStudies.CS_xweather.results-section.image.alt')" 
        class=" max-w-[310px] md:max-w-sm"/>
      </div>
      <div class="flex flex-col lg:basis-2/3">
        <div class="space-y-4">
          <h2 class="text-2xl h-auto max-w-prose">{{ $t('caseStudies.CS_xweather.results-section.title') }}</h2>
          <p class="mb-4 text-base text-slate-700 leading-relaxed" v-html="$t('caseStudies.CS_xweather.results-section.content')"></p>
        </div>
        <div class="flex flew-row gap-4 mt-6">
          <a v-for="(btn,idx) in $tm('caseStudies.CS_xweather.results-section.buttons')" :class="btn.style" :href="btn.url" target="_blank" role="button" :key="idx">{{ btn.text }}</a>
        </div>
      </div>
    </section>
    <!-- END/ Results section -->

  </div>
</template>

<script>
import {useI18n} from 'vue-i18n';
// import imageLauncherVue from '../components/imageLauncher.vue';

export default {
  name: "Contact-me",
  components: {
    // imageLauncherVue
  },
  methods: {
    isExternal(url) {
      // crude check: external if starts with http.
      // Used to determine what type of button to use (router-link vs link) .
      return /^https?:\/\//.test(url);
    }
  },
  computed: {
    // Computed properties are cached getters, not functions.
    // so we return a function to use parameters such fileName.
    imgSrc() {
      return (urlFile) => {
        try {
          return require(`../${urlFile}`);
        } catch (e) {
          return require("../assets/img-placeholder.jpg");
        }
      }
    },

    iconSrc() {
      return (urlIcon) => {
        try {
          return require(`../${urlIcon}`);
        } catch (e) {
          return require("../assets/img-placeholder.jpg");
        }
      }
    }


  },
  setup() {
    const {t} = useI18n();

    const CS_xweather_research_images = [
      {src: require('../assets/work-gallery/CS-say-hi/CS-sayhi-user-research.png'), alt: 'Different pictures showing user research activities for the sayhi app redesign'},
      {src: require('../assets/work-gallery/CS-say-hi/CS-sayhi-user-wireframe-00.png'), alt: 'Wireframe of the language selection screen'},
      {src: require('../assets/work-gallery/CS-say-hi/CS-sayhi-user-wireframe-01.png'), alt: 'Wireframe of conversation screen'},
      {src: require('../assets/work-gallery/CS-say-hi/CS-sayhi-user-wireframe-02.png'), alt: 'Wireframe of feedback component'},
      {src: require('../assets/work-gallery/CS-say-hi/CS-sayhi-user-wireframe-03.png'), alt: 'Wireframe of conversation input'}
    ]

    // const CS_xweather_handover_images = [
    //   {src: require('../assets/work-gallery/CS-say-hi/CS-sayhi-handover.png'), alt: 'General overview of the sayhi app documentation handover to development'},
    //   {src: require('../assets/work-gallery/CS-say-hi/CS-sayhi-handover-dev-04.png'), alt: 'Design documentation about conversation input'},
    //   {src: require('../assets/work-gallery/CS-say-hi/CS-sayhi-handover-dev-02.png'), alt: 'Design documentation about language selection component'},
    //   {src: require('../assets/work-gallery/CS-say-hi/CS-sayhi-handover-dev-03.png'), alt: 'Design documentation about conversation input components'}
    // ]

    return {t, CS_xweather_research_images};
  },
};
</script>
<style>

/* Hero section background gradient */
  .hero-xweather-gradient-bg {
      background-image: linear-gradient(to top, #f3e7e9 0%, #e3eeff 99%, #e3eeff 100%);
  }

/* Tasks section border gradient */

.task-border-gradient {
  background: rgba(255, 255, 255, 1);
  border-radius: 4px;
  border: 1px solid transparent;
  background-clip: padding-box;
  position: relative;
}

.task-border-gradient::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: 4px;
  padding: 1px;
  background:       linear-gradient(
        29.9672deg,
        rgb(19, 200, 255) 35.838%,
        rgb(19, 255, 208) 75.837%,
        rgba(255, 255, 255, 0.5) 135.98%
      );
  -webkit-mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
  pointer-events: none;
}
</style>
